// SimilarityView.js
import React, { useEffect, useRef, useState } from 'react';
import { LazyLoadImage } from 'react-lazy-load-image-component';
import 'react-lazy-load-image-component/src/effects/blur.css';
import WatchOptionsPanel from './WatchOptionsPanel';

const API_URL = process.env.REACT_APP_API_URL || '/api';

/**
 * Format IMDb rating for display. Handles both normal (8.8) and
 * scaled integer (88) values from DynamoDB Decimal deserialization.
 */
const formatRating = (rating) => {
    if (!rating || rating === 'N/A') return null;
    const num = typeof rating === 'string' ? parseFloat(rating) : rating;
    if (isNaN(num)) return null;
    // If rating > 10, it's likely stored as scaled integer (e.g., 88 = 8.8)
    const normalized = num > 10 ? num / 10 : num;
    return normalized.toFixed(1);
};

/**
 * Clean stringified Python list values from DynamoDB.
 * E.g., "['Drama', 'Action']" → "Drama, Action"
 */
const cleanFieldValue = (value) => {
    if (typeof value !== 'string') return value;
    const trimmed = value.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
        return trimmed.slice(1, -1).replace(/'/g, '').replace(/"/g, '').trim();
    }
    return value;
};

const bestPlot = (movie) => {
    if (!movie) return '';
    const candidates = [
        movie.wiki_plot,
        movie.wikipedia_plot,
        movie.combined_plot,
        movie.augmented_plot,
        movie.Plot,
        movie.plot,
        movie.overview,
    ];
    return candidates
        .map((value) => (value && value !== 'N/A' ? String(value).trim() : ''))
        .filter(Boolean)
        .sort((a, b) => b.length - a.length)[0] || '';
};

const TOP_K = 100

const getSimilarWsUrl = () => {
    try {
        const url = new URL(API_URL, window.location.origin);
        const wsScheme = url.protocol === 'https:' ? 'wss:' : 'ws:';
        const base = url.pathname.replace(/\/api\/?$/, '');
        return `${wsScheme}//${url.host}${base}/api/ws/similar`;
    } catch (e) {
        const wsScheme = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
        return `${wsScheme}//${window.location.host}/api/ws/similar`;
    }
};

const SimilarityView = ({ selectedMovie, onBackToResults, onMovieClick }) => {
    const [similarityData, setSimilarityData] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    const [detailMovie, setDetailMovie] = useState(null);
    const [isLoadingDetail, setIsLoadingDetail] = useState(false);
    const similarSocketRef = useRef(null);

    const referenceMovie = detailMovie ? { ...selectedMovie, ...detailMovie } : selectedMovie;
    const referencePlot = bestPlot(referenceMovie);

    useEffect(() => {
        if (selectedMovie) {
            setDetailMovie(null);
            if (selectedMovie.imdbID) {
                fetchMovieDetails(selectedMovie.imdbID);
                fetchSimilarMovies(selectedMovie.imdbID);
            } else {
                console.error('Selected movie missing imdbID:', selectedMovie);
                setError('Movie missing IMDb ID - cannot find similar movies');
                setIsLoading(false);
            }
        }
        return () => {
            if (similarSocketRef.current) {
                try { similarSocketRef.current.close(); } catch (_) {}
                similarSocketRef.current = null;
            }
        };
    }, [selectedMovie]);

    const fetchMovieDetails = async (imdbId) => {
        setIsLoadingDetail(true);
        try {
            const response = await fetch(`${API_URL}/movie/${imdbId}`);
            if (response.ok) {
                setDetailMovie(await response.json());
            }
        } catch (err) {
            console.warn('Movie detail lookup failed:', err);
        } finally {
            setIsLoadingDetail(false);
        }
    };

    const fetchSimilarMovies = async (imdbId) => {
        setIsLoading(true);
        setError(null);
        setSimilarityData({ results: {} });
        console.log('Fetching similar movies for imdbId:', imdbId);

        if (similarSocketRef.current) {
            try { similarSocketRef.current.close(); } catch (_) {}
            similarSocketRef.current = null;
        }

        let gotCategory = false;
        try {
            const ws = new WebSocket(getSimilarWsUrl());
            similarSocketRef.current = ws;

            ws.onopen = () => {
                ws.send(JSON.stringify({
                    imdb_id: imdbId,
                    top_k: TOP_K,
                    include_semantic: true,
                    max_wait_ms: 9000,
                }));
            };

            ws.onmessage = (event) => {
                if (similarSocketRef.current !== ws) return;
                let msg;
                try { msg = JSON.parse(event.data); } catch (_) { return; }

                if (msg.type === 'category' && msg.category) {
                    gotCategory = true;
                    setError(null);
                    setSimilarityData((prev) => ({
                        ...(prev || {}),
                        results: {
                            ...((prev && prev.results) || {}),
                            [msg.category]: msg.results || [],
                        },
                    }));
                } else if (msg.type === 'final') {
                    setIsLoading(false);
                    if (similarSocketRef.current === ws) similarSocketRef.current = null;
                    try { ws.close(); } catch (_) {}
                } else if (msg.type === 'error') {
                    if (!gotCategory) {
                        setError(msg.error || 'Could not load related titles.');
                    }
                    setIsLoading(false);
                    if (similarSocketRef.current === ws) similarSocketRef.current = null;
                    try { ws.close(); } catch (_) {}
                }
            };

            ws.onerror = () => {
                if (similarSocketRef.current !== ws) return;
                if (!gotCategory) setError('Could not load related titles.');
                setIsLoading(false);
                similarSocketRef.current = null;
            };

            ws.onclose = () => {
                if (similarSocketRef.current !== ws) return;
                similarSocketRef.current = null;
                setIsLoading(false);
            };
        } catch (err) {
            console.error('Error fetching similar movies:', err);
            setError(err.message);
            setIsLoading(false);
        }
    };

    const formatCategoryName = (categoryKey) => {
        if (categoryKey.startsWith('Semantic_')) {
            return 'Similar Plot';
        }
        if (categoryKey.startsWith('Genre_')) {
            const genre = categoryKey.substring(6);
            return `Genre: ${genre}`;
        }
        if (categoryKey.startsWith('Director_')) {
            const director = categoryKey.substring(9);
            return `Director: ${director}`;
        }
        if (categoryKey.startsWith('Actor_')) {
            const actor = categoryKey.substring(6);
            return `Actor: ${actor}`;
        }
        if (categoryKey.startsWith('Writer_')) {
            const writer = categoryKey.substring(7);
            return `Writer: ${writer}`;
        }
        return categoryKey;
    };

    const getYearFromMovie = (movie) => {
        if (!movie) return '';
        if (movie.Year) return movie.Year;
        if (movie.year) return String(movie.year);
        if (movie.Released && movie.Released.match(/\d{4}/)) {
            return movie.Released.match(/\d{4}/)[0];
        }
        if (movie.Title && movie.Title.match(/\(\d{4}\)/)) {
            return movie.Title.match(/\(\d{4}\)/)[0].replace(/[()]/g, '');
        }
        return '';
    };

    const getCleanTitle = (movie) => {
        const title = movie?.Title || movie?.title;
        if (!title) return 'Unknown Title';
        return title.replace(/\s*\(\d{4}\)\s*$/, '');
    };

    const handleMovieClick = (movie, event) => {
        // Check if the click was on the IMDb button (stop propagation)
        if (event.target.classList.contains('imdb-button') ||
            event.target.classList.contains('imdb-icon-button') ||
            event.target.closest('.imdb-icon-button')) {
            return; // Let the IMDb button handle its own click
        }

        // Navigate to similarity view for the clicked movie
        if (movie.imdbID && selectedMovie.imdbID !== movie.imdbID) {
            // Use the parent's onMovieClick handler to update the selected movie
            if (onMovieClick) {
                onMovieClick(movie);
            }
        }
    };

    const handleIMDbClick = (movie, event) => {
        event.stopPropagation();
        if (movie.imdbID) {
            window.open(`https://www.imdb.com/title/${movie.imdbID}/`, '_blank', 'noopener,noreferrer');
        }
    };

    // Format runtime to be more readable
    const formatRuntime = (runtime) => {
        if (!runtime) return '';
        const match = runtime.match(/(\d+)/);
        if (match) {
            const minutes = parseInt(match[1]);
            if (minutes >= 60) {
                const hours = Math.floor(minutes / 60);
                const remainingMinutes = minutes % 60;
                return `${hours}h ${remainingMinutes}m`;
            }
            return `${minutes}m`;
        }
        return runtime;
    };

    const renderMovieThumbnail = (movie, index) => (
        <div
            key={movie.imdbID || `movie-${index}`}
            className="similarity-movie-thumbnail movie-thumbnail"
            onClick={(event) => handleMovieClick(movie, event)}
        >
            <div className="similarity-poster-container poster-container">
                {movie.Poster && movie.Poster !== 'N/A' ? (
                    <LazyLoadImage
                        src={movie.Poster}
                        alt={`${movie.Title} poster`}
                        effect="blur"
                        threshold={100}
                        placeholderSrc="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
                        onError={(e) => {
                            e.target.onerror = null;
                            e.target.src = "data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII=";
                        }}
                    />
                ) : (
                    <div className="no-poster">No Image Available</div>
                )}
                <div className="title-bar">{getCleanTitle(movie)}</div>

                {/* Explicit IMDb Icon */}
                {movie.imdbID && movie.imdbID !== 'N/A' && (
                    <button
                        className="imdb-icon-button"
                        onClick={(event) => handleIMDbClick(movie, event)}
                        title="View on IMDb"
                    >
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <rect x="2" y="3" width="20" height="18" rx="2" fill="#F5C518"/>
                            <path d="M5 7h2v10H5V7zm3 0h2l1 3 1-3h2v10h-2v-6l-1 3-1-3v6H8V7zm8 0h4v2h-2v2h2v2h-2v2h2v2h-4V7z" fill="#000"/>
                        </svg>
                    </button>
                )}
            </div>

            {/* Movie details overlay (hover functionality) */}
            <div className="movie-details">
                <h3>{getCleanTitle(movie)}</h3>

                {/* Show most important details at the top */}
                {getYearFromMovie(movie) && (
                    <div className="detail-row">
                        <span className="detail-label">Year:</span>
                        <span className="detail-value">{getYearFromMovie(movie)}</span>
                    </div>
                )}

                {movie.Type && movie.Type !== 'N/A' && (
                    <div className="detail-row">
                        <span className="detail-label">Type:</span>
                        <span className="detail-value">{movie.Type.charAt(0).toUpperCase() + movie.Type.slice(1)}</span>
                    </div>
                )}

                {movie.Genre && movie.Genre !== 'N/A' && (
                    <div className="detail-row">
                        <span className="detail-label">Genre:</span>
                        <span className="detail-value">{movie.Genre}</span>
                    </div>
                )}

                {movie.Runtime && movie.Runtime !== 'N/A' && (
                    <div className="detail-row">
                        <span className="detail-label">Runtime:</span>
                        <span className="detail-value">{formatRuntime(movie.Runtime)}</span>
                    </div>
                )}

                {movie.imdbRating && movie.imdbRating !== 'N/A' && formatRating(movie.imdbRating) && (
                    <div className="detail-row">
                        <span className="detail-label">Rating:</span>
                        <span className="detail-value">{formatRating(movie.imdbRating)}/10</span>
                    </div>
                )}

                {/* Add IMDb button */}
                {movie.imdbID && movie.imdbID !== 'N/A' && (
                    <button
                        className="imdb-button"
                        onClick={(event) => handleIMDbClick(movie, event)}
                        title="View on IMDb"
                    >
                        View on IMDb
                    </button>
                )}

                {/* Show other details */}
                {Object.entries(movie).map(([key, value]) => {
                    // Skip keys we've already handled, internal fields, or empty values
                    const skipKeys = ['Title', 'Poster', 'Type', 'Genre', 'Runtime', 'imdbRating', 'Year', 'imdbID', 'Response',
                        'score', 'source', 'content', 'augmented_plot', 'wiki_plot', 'wikipedia_plot',
                        'combined_plot', 'plot', 'plot_source', 'overview', 'Plot', 'imdbVotes', 'wikipedia_url',
                        'Metacritic_score', 'Rotten_Tomatoes_score', 'overallRating', 'Rating_Category',
                        'Runtime_Minutes', 'OscarWins', 'OtherWins', 'Nominations',
                        'trailer', 'sources', 'fallback_links'];
                    if (!skipKeys.includes(key) && value && value !== 'N/A') {
                        const displayValue = cleanFieldValue(String(value));
                        const displayKey = key.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
                        return (
                            <div key={key} className="detail-row">
                                <span className="detail-label">{displayKey}:</span>
                                <span className="detail-value">{displayValue}</span>
                            </div>
                        );
                    }
                    return null;
                })}
            </div>

            <div className="similarity-movie-info">
                <h4>{getCleanTitle(movie)}</h4>
                {getYearFromMovie(movie) && <p className="year">({getYearFromMovie(movie)})</p>}
                {movie.imdbRating && movie.imdbRating !== 'N/A' && formatRating(movie.imdbRating) && (
                    <p className="rating">⭐ {formatRating(movie.imdbRating)}/10</p>
                )}
            </div>
        </div>
    );

    if (!selectedMovie) return null;

    return (
        <div className="similarity-view">
            {/* Back Button */}
            <button className="back-button" onClick={onBackToResults}>
                ← Back to Results
            </button>

            {/* Reference Movie Section */}
            <div className="reference-movie-section">
                <h1 className="similarity-title">
                    {getCleanTitle(referenceMovie)}
                </h1>

                <div className="reference-movie-card">
                    <div className="reference-poster">
                        {referenceMovie.Poster && referenceMovie.Poster !== 'N/A' ? (
                            <LazyLoadImage
                                src={referenceMovie.Poster}
                                alt={`${getCleanTitle(referenceMovie)} poster`}
                                effect="blur"
                                threshold={TOP_K}
                            />
                        ) : (
                            <div className="no-poster">No Image Available</div>
                        )}
                    </div>

                    <div className="reference-movie-details">
                        <h2>{getCleanTitle(referenceMovie)}</h2>
                        <div className="reference-metadata">
                            {getYearFromMovie(referenceMovie) && (
                                <span className="metadata-item">Year: {getYearFromMovie(referenceMovie)}</span>
                            )}
                            {referenceMovie.Genre && referenceMovie.Genre !== 'N/A' && (
                                <span className="metadata-item">Genre: {referenceMovie.Genre}</span>
                            )}
                            {referenceMovie.Director && referenceMovie.Director !== 'N/A' && (
                                <span className="metadata-item">Director: {cleanFieldValue(referenceMovie.Director)}</span>
                            )}
                            {referenceMovie.Actors && referenceMovie.Actors !== 'N/A' && (
                                <span className="metadata-item">Actors: {cleanFieldValue(referenceMovie.Actors)}</span>
                            )}
                            {referenceMovie.imdbRating && referenceMovie.imdbRating !== 'N/A' && formatRating(referenceMovie.imdbRating) && (
                                <span className="metadata-item">IMDb Rating: {formatRating(referenceMovie.imdbRating)}/10</span>
                            )}
                        </div>

                        <div className="plot-section">
                            <h4>Plot:</h4>
                            <p>
                                {referencePlot || (isLoadingDetail
                                    ? 'Loading plot...'
                                    : 'Plot not available for this title yet.')}
                            </p>
                        </div>

                        <WatchOptionsPanel movie={referenceMovie} />
                    </div>
                </div>
            </div>

            {/* Similarity Results */}
            <div className="similarity-results-section">
                {isLoading && (
                    <div className="loading-message">
                        <div className="spinner"></div>
                        <p>Finding similar movies...</p>
                    </div>
                )}

                {error && (
                    <div className="error-message">
                        <p>Error: {error}</p>
                        <button onClick={() => fetchSimilarMovies(selectedMovie.imdbID)}>
                            Try Again
                        </button>
                    </div>
                )}

                {similarityData && similarityData.results && (
                    <div className="similarity-categories">
                        {Object.entries(similarityData.results).map(([category, movies]) => {
                            if (!movies || movies.length === 0) return null;

                            return (
                                <div key={category} className="similarity-category">
                                    <h3 className="category-title">
                                        {formatCategoryName(category)}
                                        <span className="category-count">({movies.length} movies)</span>
                                    </h3>
                                    <div className="similarity-movies-grid">
                                        {movies.slice(0, TOP_K).map((movie, index) => renderMovieThumbnail(movie, index))}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}

                {similarityData && similarityData.results &&
                    !isLoading &&
                    Object.keys(similarityData.results).length === 0 &&
                    !error && (
                    <div className="no-results-message">
                        <p>No similar movies found for this title.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default SimilarityView;
